import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import {
  KeepExploring,
  RelatedComparisons,
  RelatedUseCases,
} from "../components/RelatedLinks";
import { useLang, useLp } from "../lib/i18n";
import { aiMeetingCopilotCopy } from "../lib/translations/pages/use-case-ai-meeting-copilot";

export function AiMeetingCopilot() {
  const lang = useLang();
  const lp = useLp();
  const t = aiMeetingCopilotCopy[lang];

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title={t.seo.title}
        description={t.seo.description}
        path="/use-cases/ai-meeting-copilot"
      />
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="border-b border-margin dark:border-paper/10">
          <div className="mx-auto max-w-3xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
              {t.eyebrow}
            </p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink dark:text-paper">
              {t.h1}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink/75 dark:text-paper/75">
              {t.lede}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to={lp + "/downloads"}
                className="inline-flex items-center rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
              >
                {t.cta.primary}
              </Link>
              <Link
                to={lp + "/?auth=signup&plan=free"}
                className="inline-flex items-center rounded-full border border-ink/15 dark:border-paper/15 px-6 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
              >
                {t.cta.secondary}
              </Link>
            </div>
          </div>
        </section>

        {/* The moment */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.moment.heading}
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                {t.moment.body1}
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                {t.moment.body2}
              </p>
            </div>
          </section>
        </Reveal>

        {/* How it works */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <div className="max-w-3xl">
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                  {t.how.heading}
                </h2>
                <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                  {t.how.body}
                </p>
              </div>
              <div className="mt-10 grid gap-6 md:grid-cols-3">
                {t.how.bullets.map((item) => (
                  <article
                    key={item.title}
                    className="rounded-2xl border border-ink/10 dark:border-paper/12 p-6 bg-ink/[0.02] dark:bg-paper/[0.02]"
                  >
                    <h3 className="font-display text-xl font-semibold text-ink dark:text-paper">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-[14px] leading-relaxed text-ink/75 dark:text-paper/75">
                      {item.body}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* Why notetakers are too late */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.notetakers.heading}
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                {t.notetakers.body}
              </p>
              <ul className="mt-8 space-y-4">
                {t.notetakers.bullets.map((b) => (
                  <li
                    key={b}
                    className="text-[15px] leading-relaxed text-ink/80 dark:text-paper/80"
                  >
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        {/* Use cases */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <div className="max-w-3xl">
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                  {t.useCases.heading}
                </h2>
                <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                  {t.useCases.body}
                </p>
              </div>
              <div className="mt-10 grid gap-6 md:grid-cols-3">
                {t.useCases.items.map((item) => (
                  <Link
                    key={item.href}
                    to={lp + item.href}
                    className="group block rounded-2xl border border-ink/10 dark:border-paper/12 p-6 bg-ink/[0.02] dark:bg-paper/[0.02] transition-colors hover:border-ink/25 dark:hover:border-paper/25"
                  >
                    <h3 className="font-display text-xl font-semibold text-ink dark:text-paper">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-[14px] leading-relaxed text-ink/75 dark:text-paper/75">
                      {item.body}
                    </p>
                    <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55 group-hover:text-ink dark:group-hover:text-paper">
                      {item.cta}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* Trust */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.trust.heading}
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                {t.trust.body}
              </p>
              <ul className="mt-8 space-y-4">
                {t.trust.bullets.map((b) => (
                  <li
                    key={b}
                    className="text-[15px] leading-relaxed text-ink/80 dark:text-paper/80"
                  >
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <RelatedUseCases currentId="use-case-ai-meeting-copilot" />
        </Reveal>
        <Reveal>
          <RelatedComparisons currentId="use-case-ai-meeting-copilot" />
        </Reveal>

        {/* CTA */}
        <Reveal>
          <section>
            <div className="mx-auto max-w-3xl px-6 py-24 text-center">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.cta.heading}
              </h2>
              <p className="mt-5 text-lg text-ink/70 dark:text-paper/70">
                {t.cta.body}
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to={lp + "/downloads"}
                  className="inline-flex items-center rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
                >
                  {t.cta.primary}
                </Link>
                <Link
                  to={lp + "/?auth=signup&plan=free"}
                  className="inline-flex items-center rounded-full border border-ink/15 dark:border-paper/15 px-6 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
                >
                  {t.cta.secondary}
                </Link>
              </div>
            </div>
          </section>
        </Reveal>

        <KeepExploring />
      </main>

      <Footer />
    </div>
  );
}
